"use client";

import { useMemo, useState } from "react";
import {
  ErrorNotice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  EXPRESSION_ERROR_STRINGS,
  expressionErrorMessage,
} from "@/components/tools/calculus/expression-errors";
import {
  type Constant,
  readConstant,
} from "@/components/tools/calculus/read-constant";
import { RiemannResults } from "@/components/tools/calculus/riemann-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  type Node,
  substitute,
  variable as variableNode,
} from "@/lib/tool-kit/calculus/ast";
import { compile } from "@/lib/tool-kit/calculus/evaluate";
import { type ExprError, parseExpression } from "@/lib/tool-kit/calculus/parse";
import {
  MAX_SUBINTERVALS,
  type RiemannMethod,
} from "@/lib/tool-kit/calculus/riemann";

/** English strings. A translated content file overrides these through `ui`. */
export const RIEMANN_SUM_CALCULATOR_STRINGS = {
  functionLabel: "Function f(x)",
  functionHint:
    "Use + − * / ^ and brackets. Functions: sin, cos, tan, sec, csc, cot, asin, acos, atan, sinh, cosh, tanh, exp, ln, log (base 10), sqrt, abs. Constants: pi and e.",
  lowerLabel: "Lower limit a",
  upperLabel: "Upper limit b",
  limitHint: "A number or constant such as 0, −2 or pi/2.",
  countLabel: "Number of subintervals n",
  countHint: "A whole number from 1 to {max}.",
  methodLabel: "Rule",
  methods: {
    left: "Left endpoints",
    right: "Right endpoints",
    midpoint: "Midpoint",
    trapezoid: "Trapezoid rule",
    simpson: "Simpson's rule",
  },
  results: "Results",
  approximationLabel: "Approximation",
  exactLabel: "Exact value (adaptive quadrature)",
  errorLabel: "Error (approximation − exact)",
  widthLabel: "Subinterval width h",
  noExact: "no finite value",
  evenNote:
    "Simpson's rule needs an even number of subintervals, so n = {requested} was raised to {n}.",
  formulaHeading: "The formula",
  formulas: {
    left: "L = h·[f(x₀) + f(x₁) + … + f(xₙ₋₁)], with h = {h} and xᵢ = a + i·h",
    right: "R = h·[f(x₁) + f(x₂) + … + f(xₙ)], with h = {h} and xᵢ = a + i·h",
    midpoint:
      "M = h·[f(m₁) + f(m₂) + … + f(mₙ)], with h = {h} and mᵢ the midpoint of the ith subinterval",
    trapezoid:
      "T = (h/2)·[f(x₀) + 2f(x₁) + … + 2f(xₙ₋₁) + f(xₙ)], with h = {h}",
    simpson:
      "S = (h/3)·[f(x₀) + 4f(x₁) + 2f(x₂) + … + 4f(xₙ₋₁) + f(xₙ)], with h = {h}",
  },
  sumLine: "{factor} × {weighted} = {sum}",
  tableHeading: "Sample points",
  columnIndex: "i",
  columnX: "xᵢ",
  columnMidpoint: "mᵢ",
  columnFx: "f(xᵢ)",
  columnWeight: "Weight",
  columnTerm: "Weight × f",
  skipped: "…",
  tableNote:
    "Showing the first and last rows of {count}; the sum uses every row.",
  compareHeading: "All five rules at n = {n}",
  columnRule: "Rule",
  columnValue: "Value",
  columnError: "Error",
  convergenceHeading: "How the error shrinks as n grows ({rule})",
  columnN: "n",
  convergenceNote:
    "Doubling n cuts the error of the left and right sums about 2×, of the midpoint and trapezoid rules about 4×, and of Simpson's rule about 16×.",
  graphHeading: "Graph",
  graphTitle:
    "Graph of y = f(x) with the {rule} approximation from {lower} to {upper} drawn in",
  seriesFunction: "y = f(x)",
  seriesShapes: "{rule}, n = {n}",
  downloadPng: "Download graph (PNG)",
  downloadSvg: "Download graph (SVG)",
  undefinedAt:
    "f has no real value at x = {at}, so this rule cannot be evaluated here.",
  limitInvalid:
    "Enter each limit as a number or constant such as 0, −2 or pi/2.",
  limitOrder: "The lower limit must be less than the upper limit.",
  countInvalid: "Enter n as a whole number from 1 to {max}.",
  oneVariable: "Use a function of one variable, such as x^2.",
  enterLimits: "Enter both limits.",
  expressionErrors: EXPRESSION_ERROR_STRINGS,
} as const;

export type RiemannStrings = typeof RIEMANN_SUM_CALCULATOR_STRINGS;

export type RiemannView =
  | { kind: "problem"; message: string }
  | {
      kind: "ok";
      ast: Node;
      f: (x: number) => number;
      lower: Constant;
      upper: Constant;
      decimals: boolean;
      method: RiemannMethod;
      requested: number;
    };

const METHODS: readonly RiemannMethod[] = [
  "left",
  "right",
  "midpoint",
  "trapezoid",
  "simpson",
];

function limitProblem(
  lower: Constant | "invalid" | null,
  upper: Constant | "invalid" | null,
  strings: RiemannStrings
): string | null {
  if (lower === null || upper === null) {
    return strings.enterLimits;
  }
  if (lower === "invalid" || upper === "invalid") {
    return strings.limitInvalid;
  }
  return lower.value < upper.value ? null : strings.limitOrder;
}

function buildView(
  inputs: {
    text: string;
    lowerText: string;
    upperText: string;
    countText: string;
    method: RiemannMethod;
  },
  strings: RiemannStrings
): RiemannView {
  const parsed = parseExpression(inputs.text);
  if (!parsed.ok) {
    return {
      kind: "problem",
      message: expressionErrorMessage(
        parsed.error as ExprError,
        strings.expressionErrors
      ),
    };
  }
  if (parsed.variables.length > 1) {
    return { kind: "problem", message: strings.oneVariable };
  }
  const lower = readConstant(inputs.lowerText);
  const upper = readConstant(inputs.upperText);
  const problem = limitProblem(lower, upper, strings);
  if (
    problem ||
    !lower ||
    !upper ||
    lower === "invalid" ||
    upper === "invalid"
  ) {
    return { kind: "problem", message: problem ?? strings.limitInvalid };
  }
  const count = parseIntegerInput(inputs.countText);
  if (count === null || count < 1 || count > MAX_SUBINTERVALS) {
    return {
      kind: "problem",
      message: t(strings.countInvalid, { max: MAX_SUBINTERVALS }),
    };
  }
  const original = parsed.variables[0] ?? "x";
  const ast =
    original === "x"
      ? parsed.ast
      : substitute(parsed.ast, original, variableNode("x"));
  return {
    kind: "ok",
    ast,
    f: compile(ast, "x"),
    lower,
    upper,
    decimals: parsed.usesDecimals,
    method: inputs.method,
    requested: count,
  };
}

export default function RiemannSumCalculatorWidget() {
  const strings = useToolUi(RIEMANN_SUM_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("x^2");
  const [lowerText, setLowerText] = useState("0");
  const [upperText, setUpperText] = useState("2");
  const [countText, setCountText] = useState("4");
  const [method, setMethod] = useState<RiemannMethod>("left");
  const view = useMemo(
    () => buildView({ text, lowerText, upperText, countText, method }, strings),
    [text, lowerText, upperText, countText, method, strings]
  );
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <TextField
            hint={strings.functionHint}
            id="rs-function"
            label={strings.functionLabel}
            mono
            onChange={setText}
            value={text}
          />
        </div>
        <TextField
          id="rs-lower"
          label={strings.lowerLabel}
          mono
          onChange={setLowerText}
          value={lowerText}
        />
        <TextField
          hint={strings.limitHint}
          id="rs-upper"
          label={strings.upperLabel}
          mono
          onChange={setUpperText}
          value={upperText}
        />
        <TextField
          hint={t(strings.countHint, { max: MAX_SUBINTERVALS })}
          id="rs-count"
          label={strings.countLabel}
          mono
          numeric
          onChange={setCountText}
          value={countText}
        />
        <SelectField
          id="rs-method"
          label={strings.methodLabel}
          onChange={setMethod}
          options={METHODS.map((value) => ({
            value,
            label: strings.methods[value],
          }))}
          value={method}
        />
      </div>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {view.kind === "problem" ? (
          <ErrorNotice>{view.message}</ErrorNotice>
        ) : null}
        {view.kind === "ok" ? (
          <RiemannResults fmt={fmt} strings={strings} view={view} />
        ) : null}
      </section>
    </>
  );
}
